import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/** A new comparison's starting values, such as a failed comparison being tried again. */
export type ComparisonDraft = {
  homeMunicipalityId: number;
  comparisonMunicipalityId: number;
  prompt: string;
};

type CodeLibraryCompareContextValue = {
  /** The open comparison session, or null for the new comparison form. */
  activeComparisonId: number | null;
  openComparison: (id: number) => void;
  startNewComparison: (draft?: ComparisonDraft) => void;
  draft: ComparisonDraft | null;
};

const CodeLibraryCompareContext = createContext<CodeLibraryCompareContextValue | null>(null);

export const COMPARISON_HISTORY_QUERY_KEY = ["code_library_comparisons"] as const;
export const comparisonQueryKey = (id: number) => ["code_library_comparison", id] as const;

export function CodeLibraryCompareProvider({ children }: { children: React.ReactNode }) {
  const [activeComparisonId, setActiveComparisonId] = useState<number | null>(null);
  const [draft, setDraft] = useState<ComparisonDraft | null>(null);

  const openComparison = useCallback((id: number) => {
    setDraft(null);
    setActiveComparisonId(id);
  }, []);

  const startNewComparison = useCallback((nextDraft?: ComparisonDraft) => {
    setDraft(nextDraft ?? null);
    setActiveComparisonId(null);
  }, []);

  const value = useMemo(
    () => ({ activeComparisonId, openComparison, startNewComparison, draft }),
    [activeComparisonId, openComparison, startNewComparison, draft],
  );

  return <CodeLibraryCompareContext.Provider value={value}>{children}</CodeLibraryCompareContext.Provider>;
}

export function useCodeLibraryCompare() {
  const ctx = useContext(CodeLibraryCompareContext);
  if (!ctx) {
    throw new Error("useCodeLibraryCompare must be used within CodeLibraryCompareProvider");
  }
  return ctx;
}
